import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageSquareText, Send, ShieldCheck, PlugZap, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function TwilioCard() {
  const [state, setState] = useState({
    connected: false,
    account_sid_masked: "",
    from_number: "",
    whatsapp_from_number: "",
    whatsapp_verified_at: null,
    whatsapp_verified_to: null,
    last_test_at: null,
    last_test_ok: null,
    checkin_sms_enabled: true,
  });
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [whatsappFromNumber, setWhatsappFromNumber] = useState("");
  const [whatsappTestTo, setWhatsappTestTo] = useState("");
  const [whatsappVerifying, setWhatsappVerifying] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/twilio");
      setState(data);
      setFromNumber(data.from_number || "");
      setWhatsappFromNumber(data.whatsapp_from_number || "");
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const toggleSignature = async (v) => {
    try {
      await api.put("/settings/twilio/signature-toggle", { enabled: v });
      setState((s) => ({ ...s, require_signature: v }));
      toast.success(v ? "Signature check on" : "Signature check paused");
    } catch (err) { toast.error(apiError(err)); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!accountSid.trim() && !authToken.trim() && !fromNumber.trim() && whatsappFromNumber === (state.whatsapp_from_number || "")) {
      toast.error("Add your Twilio Account SID, Auth Token and From number");
      return;
    }
    setBusy(true);
    try {
      const body = {
        from_number: fromNumber.trim(),
        whatsapp_from_number: whatsappFromNumber.trim(),
      };
      if (accountSid.trim()) body.account_sid = accountSid.trim();
      if (authToken.trim()) body.auth_token = authToken.trim();
      await api.put("/settings/twilio", body);
      toast.success("Twilio credentials saved");
      setAccountSid("");
      setAuthToken("");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const verifyWhatsapp = async () => {
    if (!whatsappTestTo) { toast.error("Enter a WhatsApp-verified mobile number in +447… format"); return; }
    setWhatsappVerifying(true);
    try {
      await api.post("/settings/twilio/whatsapp/verify", { to: whatsappTestTo });
      toast.success(`WhatsApp verified — sent to ${whatsappTestTo}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err, "WhatsApp verification failed"));
    } finally { setWhatsappVerifying(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a mobile number in +447… format"); return; }
    setTesting(true);
    try {
      await api.post("/settings/twilio/test", {
        to: testTo,
        body: "Newport Stays: your SMS reminder wiring works. Reply STOP to opt out.",
      });
      toast.success(`Test SMS sent to ${testTo}`);
      await load();
    } catch (err) { toast.error(apiError(err, "Twilio rejected the message")); }
    finally { setTesting(false); }
  };

  const verify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post("/settings/twilio/verify");
      setVerifyResult({ ok: true, ...data });
      toast.success(
        data.healed_prefix
          ? "Auth token healed (US prefix stripped) — credentials valid ✔"
          : `Twilio credentials valid — account ${data.account_status}`,
      );
    } catch (err) {
      setVerifyResult({ ok: false, detail: apiError(err) });
      toast.error(apiError(err, "Twilio rejected the credentials"));
    } finally { setVerifying(false); }
  };

  const toggleCheckin = async (v) => {
    try {
      await api.put("/settings/twilio/checkin-toggle", { enabled: v });
      setState((s) => ({ ...s, checkin_sms_enabled: v }));
      toast.success(v ? "Check-in SMS enabled" : "Check-in SMS paused");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="ns-card p-8" data-testid="twilio-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <MessageSquareText className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="ns-label">SMS reminders</div>
          <div className="font-serif text-2xl">Twilio</div>
        </div>
        {state.connected && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-emerald-50 text-emerald-800 border border-emerald-100">
            <ShieldCheck className="w-3.5 h-3.5" /> Connected
          </span>
        )}
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-4">
        Newport Stays texts every arriving guest their door code, Wi-Fi and any special instructions at 08:15 UTC on
        check-in day. Paste your Twilio credentials to switch it on — the Auth Token stays encrypted at rest.
      </p>

      <form onSubmit={save} className="grid gap-4 max-w-xl">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="ns-label">Account SID</Label>
            <Input
              data-testid="twilio-account-sid"
              className="mt-2 h-11 bg-white font-mono text-xs"
              placeholder={state.account_sid_masked || "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
              value={accountSid}
              onChange={(e) => setAccountSid(e.target.value)}
            />
          </div>
          <div>
            <Label className="ns-label">Auth token</Label>
            <Input
              data-testid="twilio-auth-token"
              type="password"
              className="mt-2 h-11 bg-white font-mono text-xs"
              placeholder={state.connected ? "••••••••••••" : "Twilio auth token"}
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label className="ns-label">From number (E.164)</Label>
          <Input
            data-testid="twilio-from-number"
            className="mt-2 h-11 bg-white font-mono text-xs"
            placeholder="+441234567890"
            value={fromNumber}
            onChange={(e) => setFromNumber(e.target.value)}
          />
          <div className="text-[11px] text-slate-400 mt-1.5">
            Must be a Twilio-owned number or Messaging Service SID with SMS capability.
          </div>
        </div>
        <div>
          <Button type="submit" disabled={busy} data-testid="twilio-save-btn" className="rounded-full ns-btn-primary h-11 px-6">
            {busy ? "Saving…" : state.connected ? "Update credentials" : "Save"}
          </Button>
          {state.connected && (
            <Button
              type="button"
              onClick={verify}
              disabled={verifying}
              variant="outline"
              data-testid="twilio-verify-btn"
              className="rounded-full h-11 px-5 ml-2"
            >
              <PlugZap className="w-4 h-4 mr-2" />
              {verifying ? "Verifying…" : "Verify credentials"}
            </Button>
          )}
          {verifyResult && (
            <div
              data-testid="twilio-verify-result"
              className={`mt-3 text-xs ${verifyResult.ok ? "text-emerald-700" : "text-red-600"}`}
            >
              {verifyResult.ok
                ? `✓ Twilio account ${verifyResult.account_status}${verifyResult.friendly_name ? " · " + verifyResult.friendly_name : ""}${verifyResult.healed_prefix ? " (auth token had US prefix — auto-stripped)" : ""}`
                : `✗ ${verifyResult.detail}`}
            </div>
          )}
        </div>
      </form>

      <div className="ns-hairline my-6" />

      <div className="grid gap-3 max-w-xl">
        <Label className="ns-label">Send a test SMS</Label>
        <div className="flex gap-2 flex-wrap">
          <Input
            data-testid="twilio-test-to"
            className="h-11 bg-white flex-1 min-w-[220px]"
            placeholder="+447700900123"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
          <Button
            data-testid="twilio-test-btn"
            variant="outline"
            disabled={testing || !state.connected}
            onClick={sendTest}
            className="rounded-full h-11 px-5"
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? "Sending…" : "Send test"}
          </Button>
        </div>
        {state.last_test_at && (
          <div className={`text-xs ${state.last_test_ok ? "text-emerald-700" : "text-red-600"}`}>
            Last test: {state.last_test_ok ? "Delivered" : "Failed"} · {new Date(state.last_test_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="ns-hairline my-6" />

      <div className="grid gap-3 max-w-xl" data-testid="twilio-whatsapp-wizard">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-emerald-600" />
          <Label className="ns-label">WhatsApp sender (optional)</Label>
        </div>
        <p className="text-[11px] text-slate-400 -mt-1">
          For cleaning-rota broadcasts. Leave blank to reuse the SMS From number (only works if it&apos;s WhatsApp-enabled). For sandbox testing use <code className="bg-stone-100 px-1 rounded">+14155238886</code>.
        </p>
        <Input
          data-testid="twilio-whatsapp-from-number"
          className="h-11 bg-white font-mono text-xs"
          placeholder="+14155238886"
          value={whatsappFromNumber}
          onChange={(e) => setWhatsappFromNumber(e.target.value)}
        />
        <div className="flex gap-2 flex-wrap items-center">
          <Input
            data-testid="twilio-whatsapp-test-to"
            className="h-11 bg-white flex-1 min-w-[220px]"
            placeholder="Send test to +447700900123"
            value={whatsappTestTo}
            onChange={(e) => setWhatsappTestTo(e.target.value)}
          />
          <Button
            type="button"
            data-testid="twilio-whatsapp-verify-btn"
            variant="outline"
            onClick={verifyWhatsapp}
            disabled={whatsappVerifying || !state.connected}
            className="rounded-full h-11 px-5"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            {whatsappVerifying ? "Verifying…" : "Verify WhatsApp"}
          </Button>
        </div>
        {state.whatsapp_verified_at && (
          <div
            data-testid="twilio-whatsapp-verified-badge"
            className="text-[11px] text-emerald-700"
          >
            ✓ WhatsApp verified · last sent to {state.whatsapp_verified_to || "—"} · {new Date(state.whatsapp_verified_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="ns-hairline my-6" />
      <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer">
        <input
          data-testid="twilio-checkin-toggle"
          type="checkbox"
          checked={state.checkin_sms_enabled !== false}
          onChange={(e) => toggleCheckin(e.target.checked)}
          className="w-4 h-4 accent-teal-600"
        />
        <span>
          Automatically SMS today&apos;s arriving guests with the door code
          <span className="block text-[11px] text-slate-400">Runs every day at 08:15 UTC. Untick to pause.</span>
        </span>
      </label>
      <div className="ns-hairline my-6" />
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <input
          data-testid="twilio-signature-toggle"
          type="checkbox"
          checked={state.require_signature !== false}
          onChange={(e) => toggleSignature(e.target.checked)}
          className="w-4 h-4 accent-teal-600"
        />
        <div>
          Reject inbound SMS webhooks without a valid <code>X-Twilio-Signature</code>
          <span className="block text-[11px] text-slate-400">
            Recommended on. Uncheck only while testing from tools like curl or Postman.
          </span>
        </div>
      </div>
    </div>
  );
}
